/**
 * MGI-PILOT-SELF-SERVICE-CREDENTIAL-ROTATION-WS1 — the client side of THE one self-service credential rotation.
 *
 * The backend (`POST /api/auth/change-password`, BE auth router) is the authority: it rotates ONLY the caller's own
 * credential, proves the current one against the persisted hash, applies the policy below and revokes every
 * pre-rotation session. This module only mirrors what the user can be told BEFORE a request is sent. It names no
 * target (there is none to name), stores nothing, and never logs a value.
 */

/** Mirror of `SELF_ROTATION_PASSWORD_POLICY` in the backend (middleware/auth.js). The backend decides. */
export const SELF_ROTATION_POLICY = Object.freeze({ minLength: 8, maxBytes: 72 })

/** The sold roles the backend admits on the route (contract §1.1). The menu entry is offered only to them. */
export const SELF_ROTATION_ROLES = Object.freeze(['agency_employee', 'field_supervisor', 'department_head'] as const)

/** The one self-service route, relative to the API base the canonical client resolves. */
export const SELF_ROTATION_ENDPOINT = '/auth/change-password'

export function canSelfRotate(role: string | null | undefined): boolean {
  return (SELF_ROTATION_ROLES as readonly string[]).includes(String(role || ''))
}

const utf8Bytes = (s: string): number => new TextEncoder().encode(s).length

/** The first reason the form must not be submitted yet, or null. Never includes a value. */
export function selfRotationFormError(currentPassword: string, newPassword: string, confirmPassword: string): string | null {
  if (!currentPassword) return 'Escribe tu contraseña actual.'
  if (!newPassword) return 'Escribe la nueva contraseña.'
  if (newPassword.length < SELF_ROTATION_POLICY.minLength) return `La nueva contraseña debe tener al menos ${SELF_ROTATION_POLICY.minLength} caracteres.`
  if (utf8Bytes(newPassword) > SELF_ROTATION_POLICY.maxBytes) return `La nueva contraseña no puede superar ${SELF_ROTATION_POLICY.maxBytes} bytes.`
  if (newPassword === currentPassword) return 'La nueva contraseña debe ser distinta de la actual.'
  if (newPassword !== confirmPassword) return 'La confirmación no coincide con la nueva contraseña.'
  return null
}

export type SelfRotationOutcome =
  | { ok: true }
  | { ok: false; status: number; code: string | null }

/** User-facing message for a refused rotation (by the backend's stable code / status). */
export function selfRotationRefusalMessage(outcome: { status: number; code: string | null }): string {
  switch (outcome.code) {
    case 'CURRENT_PASSWORD_INVALID': return 'La contraseña actual no es correcta.'
    case 'NEW_PASSWORD_POLICY': return `La nueva contraseña debe tener entre ${SELF_ROTATION_POLICY.minLength} caracteres y ${SELF_ROTATION_POLICY.maxBytes} bytes.`
    case 'NEW_PASSWORD_SAME_AS_CURRENT': return 'La nueva contraseña debe ser distinta de la actual.'
    default: break
  }
  if (outcome.status === 429) return 'Demasiados intentos. Espera un minuto antes de volver a intentarlo.'
  if (outcome.status === 401) return 'Tu sesión ya no es válida. Inicia sesión de nuevo.'
  if (outcome.status === 403) return 'Tu perfil no puede cambiar la contraseña desde aquí.'
  return 'No se pudo cambiar la contraseña. Inténtalo de nuevo.'
}
